import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Continent CRUD page in a tab.
 */
const CONTINENT_CRUD_ROUTE = "/home/countries/continentConfigCrud";

//endregion

//region Component

/**
 * Renders the Continents configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in compact tab mode;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the Continents MTable;
 * - use the Locations permission module;
 * - expose only the View workflow for Continents.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Continents configuration tab.
 */
export default function ContinentConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this configuration tab.
     *
     * Important:
     * - Continents are treated as read-only here;
     * - MTable uses "Update" internally for the Edit action, so "Update" must be
     *   removed instead of "Edit";
     * - Audit is hidden to keep this tab focused on the View workflow only.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy", "Create", "Update", "Delete", "Audit"],
        []
    );

    /**
     * Opens the Continent CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected continent row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected continent row.
     * @returns {void}
     */
    const openContinentCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "ContinentLine",
                        value: row,
                    })
                );
            }

            const safeName = row?.Name ?? "";

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewContinent")
                    : operation === "View"
                        ? `${t("LabelViewContinent")} "${safeName}"`
                        : `${t("LabelEditContinent")} "${safeName}"`;

            onCreateTab(CONTINENT_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Continent workflow.
     *
     * Only View is intentionally exposed for this tab.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openContinentCrud("View", row)],
            ]),
        [openContinentCrud]
    );

    return (
        <div
            className="config-page config-page--tabs"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("Continents")}
                </div>

                <div className="config-page__description">
                    {t("InfoContinents")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="Continents"
                    roleName="Locations"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

ContinentConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion